/**
 * Status constants for SharePoint file workflow.
 * These match the backend status_constants.py
 */

// File upload statuses
export const STATUS_QUEUED = "queued";
export const STATUS_UPLOADING = "uploading";
export const STATUS_UPLOADED = "uploaded";

// Analysis statuses
export const STATUS_ANALYZING = "analyzing";
export const STATUS_ANALYZED = "analyzed";
export const STATUS_ANALYSIS_ERROR = "analysis_error";

// Approval statuses
export const STATUS_FILENAME_APPROVED = "filename_approved";
export const STATUS_PENDING_FILENAME_APPROVAL = "pending_filename_approval";

// SharePoint push statuses
export const STATUS_PUSHING_TO_SHAREPOINT = "pushing_to_sharepoint";
export const STATUS_PUSHED = "pushed";
export const STATUS_PUSHED_TO_SHAREPOINT_DEPRECATED = "pushed_to_sharepoint";
export const STATUS_PUSH_ERROR = "push_error";

// Summary approval statuses
export const STATUS_PENDING_SUMMARY_APPROVAL = "pending_summary_approval";
export const STATUS_SUMMARY_APPROVED = "summary_approved";

// Final statuses
export const STATUS_DONE = "done";
export const STATUS_DELETING = "deleting";
export const STATUS_DELETE_ERROR = "delete_error";

// Workflow job creation status
export const STATUS_CREATING_WORKFLOW_JOB = "creating_workflow_job";

/**
 * Get human-readable display name for a status
 * @param {string} status - Status constant
 * @param {string|null} statusDetail - Optional detail message
 * @returns {string} Human-readable status name
 */
export function getStatusDisplayName(status, statusDetail = null) {
  // If there's a status detail, show it
  if (statusDetail) {
    return statusDetail;
  }

  const displayNames = {
    [STATUS_QUEUED]: "Queued",
    [STATUS_UPLOADING]: "Uploading",
    [STATUS_UPLOADED]: "Uploaded",
    [STATUS_CREATING_WORKFLOW_JOB]: "Starting Analysis",
    [STATUS_ANALYZING]: "Analyzing",
    [STATUS_ANALYZED]: "Analyzed - Ready to Review",
    [STATUS_PENDING_FILENAME_APPROVAL]: "Pending Filename Approval",
    [STATUS_FILENAME_APPROVED]: "Filename Approved",
    [STATUS_PUSHING_TO_SHAREPOINT]: "Pushing to SharePoint",
    [STATUS_PUSHED]: "Pushed - Pending Summary Review",
    [STATUS_PUSHED_TO_SHAREPOINT_DEPRECATED]: "Pushed - Pending Summary Review",
    [STATUS_PENDING_SUMMARY_APPROVAL]: "Pending Summary Approval",
    [STATUS_SUMMARY_APPROVED]: "Summary Approved",
    [STATUS_DONE]: "Done",
    [STATUS_ANALYSIS_ERROR]: "Analysis Error",
    [STATUS_PUSH_ERROR]: "Push Error",
    [STATUS_DELETING]: "Deleting",
    [STATUS_DELETE_ERROR]: "Delete Error",
  };

  return displayNames[status] || status?.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase()) || "Unknown";
}

/**
 * Get Bootstrap color class for a status
 * @param {string} status - Status constant
 * @returns {string} Bootstrap color class (bg-primary, bg-success, etc.)
 */
export function getStatusColorClass(status) {
  switch (status) {
    case STATUS_ANALYSIS_ERROR:
    case STATUS_PUSH_ERROR:
    case STATUS_DELETE_ERROR:
      return "bg-danger";
    
    case STATUS_QUEUED:
    case STATUS_UPLOADING:
      return "bg-info";
    
    case STATUS_ANALYZING:
    case STATUS_CREATING_WORKFLOW_JOB:
      return "bg-primary";
    
    case STATUS_ANALYZED:
    case STATUS_PENDING_FILENAME_APPROVAL:
      return "bg-warning";
    
    case STATUS_FILENAME_APPROVED:
      return "bg-info";
    
    case STATUS_PUSHING_TO_SHAREPOINT:
      return "bg-primary";
    
    case STATUS_PUSHED:
    case STATUS_PUSHED_TO_SHAREPOINT_DEPRECATED:
    case STATUS_PENDING_SUMMARY_APPROVAL:
      return "bg-warning";
    
    case STATUS_SUMMARY_APPROVED:
    case STATUS_DONE:
      return "bg-success";
    
    case STATUS_UPLOADED:
      return "bg-secondary";
    
    case STATUS_DELETING:
      return "bg-secondary";
    
    default:
      return "bg-secondary";
  }
}

/**
 * Check if a file requires filename approval
 * @param {string} status - File status
 * @returns {boolean} True if filename approval is required
 */
export function requiresFilenameApproval(status) {
  return status === STATUS_ANALYZED || status === STATUS_PENDING_FILENAME_APPROVAL;
}

/**
 * Check if a file requires summary approval
 * @param {string} status - File status
 * @returns {boolean} True if summary approval is required
 */
export function requiresSummaryApproval(status) {
  return status === STATUS_PUSHED || status === STATUS_PUSHED_TO_SHAREPOINT_DEPRECATED || status === STATUS_PENDING_SUMMARY_APPROVAL;
}

/**
 * Check if a file can be pushed to SharePoint
 * @param {string} status - File status
 * @returns {boolean} True if file can be pushed
 */
export function canPushToSharePoint(status) {
  return status === STATUS_FILENAME_APPROVED || status === STATUS_ANALYZED;
}

/**
 * Check if filename can be approved
 * @param {string} status - File status
 * @returns {boolean} True if filename can be approved
 */
export function canApproveFilename(status) {
  return status === STATUS_ANALYZED || status === STATUS_PENDING_FILENAME_APPROVAL;
}

/**
 * Check if summary can be approved
 * @param {string} status - File status
 * @returns {boolean} True if summary can be approved
 */
export function canApproveSummary(status) {
  return status === STATUS_PUSHED || status === STATUS_PUSHED_TO_SHAREPOINT_DEPRECATED || status === STATUS_PENDING_SUMMARY_APPROVAL;
}

/**
 * Check if file can be re-analyzed
 * @param {string} status - File status
 * @returns {boolean} True if file can be re-analyzed
 */
export function canReanalyze(status) {
  return status === STATUS_UPLOADED || 
         status === STATUS_ANALYZED || 
         status === STATUS_ANALYSIS_ERROR ||
         status === STATUS_PENDING_FILENAME_APPROVAL ||
         status === STATUS_FILENAME_APPROVED;
}

/**
 * Check if file is in a terminal state (done or error that can't continue)
 * @param {string} status - File status
 * @returns {boolean} True if status is terminal
 */
export function isTerminalStatus(status) {
  return status === STATUS_DONE || status === STATUS_DELETE_ERROR;
}

/**
 * Check if file is in an error state
 * @param {string} status - File status
 * @returns {boolean} True if status indicates an error
 */
export function isErrorStatus(status) {
  return status === STATUS_ANALYSIS_ERROR || 
         status === STATUS_PUSH_ERROR || 
         status === STATUS_DELETE_ERROR;
}

/**
 * Check if file is currently processing (in progress)
 * @param {string} status - File status
 * @returns {boolean} True if status indicates processing
 */
export function isProcessingStatus(status) {
  return status === STATUS_QUEUED ||
         status === STATUS_UPLOADING ||
         status === STATUS_ANALYZING ||
         status === STATUS_CREATING_WORKFLOW_JOB ||
         status === STATUS_PUSHING_TO_SHAREPOINT ||
         status === STATUS_DELETING;
}
